import {
  Button,
  Center,
  Group,
  Loader,
  ScrollArea,
  Space,
  Stack,
  Text
} from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { usePollCameraCreationStatus } from '@/queries/cameraQueries'
import { generateUUID } from '@/utils/uuid'
import { ImportProgressBar } from '../ImportProgressBar/ImportProgressBar'
import { ImportStatusIndicator } from '../ImportStatusIndicator/ImportStatusIndicator'

export type ImportCompletionStatus = 'success' | 'failed'

type ImportProgressHandlerProps = {
  deviceId: string
  onCompletion: (status: ImportCompletionStatus) => void
  onBack: () => void
  onClose: () => void
}

export const ImportProgressHandler = ({
  deviceId,
  onCompletion,
  onBack,
  onClose
}: ImportProgressHandlerProps) => {
  const pollTaskId = useRef(generateUUID())

  const {
    data: cameraStatusData,
    isError,
    error,
    isLoading
  } = usePollCameraCreationStatus({
    deviceId,
    pollTaskId: pollTaskId.current,
    enabled: !!deviceId && !!pollTaskId.current
  })

  useEffect(() => {
    if (
      cameraStatusData?.status === 'Complete' ||
      cameraStatusData?.status === 'Failed'
    ) {
      onCompletion(
        cameraStatusData?.status === 'Complete' ? 'success' : 'failed'
      )
    }
  }, [cameraStatusData]) // eslint-disable-line react-hooks/exhaustive-deps
  // do not include onCompletion in the dependency array because it will cause a infinite loop

  return (
    <Stack>
      {isError && (
        <PlatformAlert variant="error" compact>
          {getApiError(error)?.errorMessage || (
            <FormattedMessage id="cameras.import.error" />
          )}
        </PlatformAlert>
      )}

      {isLoading && (
        <Center>
          <Loader />
        </Center>
      )}

      {cameraStatusData && (
        <Stack>
          <Space h="xl" />

          <ImportStatusIndicator status={cameraStatusData.status} />

          <ImportProgressBar
            total={cameraStatusData.total_tasks}
            imported={cameraStatusData.completed_tasks}
          />

          <ScrollArea h={240}>
            {cameraStatusData.failed_rtsp_urls.length > 0 && (
              <PlatformAlert variant="error" showIcon={false} compact>
                <Stack gap="xs">
                  {cameraStatusData.failed_rtsp_urls.map((url) => (
                    <Group key={url} wrap="nowrap" gap="xs" align="start">
                      <IconX size={16} color="var(--mantine-color-red-8)" />
                      <Text size="xs">{url}</Text>
                    </Group>
                  ))}
                </Stack>
              </PlatformAlert>
            )}
          </ScrollArea>

          <Group justify="center">
            {cameraStatusData.status === 'Failed' && (
              <Button variant="outline" miw={120} onClick={onBack}>
                <FormattedMessage id="tryAgain" />
              </Button>
            )}

            {cameraStatusData.status !== 'Pending' && (
              <Button miw={120} onClick={onClose}>
                <FormattedMessage id="close" />
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Stack>
  )
}
